import { Unarray } from '@pukka/common/types';
import { dateUtils, numberUtils } from '@pukka/common/utils';
import { TableV2 } from '@pukka/ui-components';
import { ColumnDef, TableOptions, getExpandedRowModel, getGroupedRowModel } from '@tanstack/react-table';
import { useSnapshot } from 'valtio';

import { OldDataDisplay } from './OldDataDisplay';
import { SummaryHeader } from './SummaryHeader';
import {
  ITransactionVoidState,
  TransactionPersistedState,
  TransactionState,
  VoidTransactionState,
  useVoidTransactionData,
} from './Transaction.state';
import { getIsDataTooOld } from './utils';

type IData = Unarray<ITransactionVoidState['data']>;

const columns: ColumnDef<IData>[] = [
  {
    header: 'Void Time',
    accessorKey: 'date',
    cell(props) {
      return props.row.getIsGrouped() ? props.row.groupingValue : dateUtils.formatDate(props.getValue() as string, 'yyyy-MM-dd HH:mm:ss');
    },
  },
  {
    header: 'Order ID',
    accessorKey: 'orderId',
  },
  {
    header: 'Item Name',
    accessorKey: 'itemName',
  },
  {
    header: 'Quantity',
    accessorKey: 'quantity',
  },
  {
    header: 'Void Reason',
    accessorKey: 'reason',
  },
  {
    header: 'Void By',
    accessorKey: 'by',
  },
  {
    accessorKey: 'group',
  },
  {
    header: 'Total Price',
    accessorKey: 'total',
    meta: {
      className: 'text-right',
    },
    cell(props) {
      return numberUtils.formatCurrency(props.getValue() as number, 'Rp.');
    },
  },
];

const options: Partial<TableOptions<IData>> = {
  getExpandedRowModel: getExpandedRowModel(),
  getGroupedRowModel: getGroupedRowModel(),
  groupedColumnMode: 'remove',
  initialState: {
    grouping: ['group'],
    expanded: true,
  },
  defaultColumn: {
    size: 50,
  },
};

function VoidTransactionPagelet() {
  const { void: _void } = useSnapshot(TransactionState);
  const { data, error, status } = useSnapshot(VoidTransactionState);
  const { endDate, startDate } = useSnapshot(TransactionPersistedState);

  const _status = useVoidTransactionData();
  const isDataTooOld = getIsDataTooOld(endDate, startDate);

  return (
    <>
      <SummaryHeader loading={_status === 'loading'} className="mb-4">
        <SummaryHeader.Item title="TOTAL VOID ITEMS" sum={_void?.count ?? 0} />
        <SummaryHeader.Item title="TOTAL VOID PRICE" sum={_void?.total ?? 0} inCurrencyFormat />
      </SummaryHeader>
      <TableV2 columns={columns} data={data as IData[]} bordered rounded options={options} className="table-fixed">
        <TableV2.Header />
        {status === 'success' || data.length > 0 ? (
          <TableV2.Body noDataPlaceholder={isDataTooOld && data.length === 0 ? <OldDataDisplay /> : 'No Data Found'} />
        ) : null}
        {status === 'loading' ? <TableV2.Loading /> : null}
        {status === 'error' ? <TableV2.Error>{error}</TableV2.Error> : null}
      </TableV2>
    </>
  );
}

export { VoidTransactionPagelet };
